import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { TanStackTable } from '@/components/table/TanStackTable';
import { SettingContentCard } from '@/sections/settings/components/primitives/SettingContentCard';
import { Account } from '@/types';

import { getEmailColumns } from './columns/emailColumns';

export interface EmailNotificationSectionProps {
  /** Filtered email notifications only */
  notifications: Account[];
  /** Handler for viewing/editing severity (opens EmailViewModal) */
  onView: (integration: Account) => void;
  /** Handler for deleting an email notification (hidden when undefined) */
  onDelete?: (integration: Account) => void;
  /** Handler for adding a new email notification (hidden when undefined) */
  onAdd?: () => void;
  /** Loading status */
  status?: 'error' | 'success' | 'pending';
  /** Error object if status is error */
  error?: Error | null;
}

/**
 * Email Notification Section
 *
 * Displays configured email notifications in a TanStackTable with Edit/Delete actions.
 * Uses persistent header-in-card pattern (Option C) where header is always visible.
 *
 * Structure:
 * - Header Row: Always visible with icon, title, description, and "Add Email" button
 * - Content Row: TanStackTable shown only when emails exist (conditional)
 *
 * Used in:
 * - NotificationSettingsCards
 */
export const EmailNotificationSection: React.FC<
  EmailNotificationSectionProps
> = ({
  notifications,
  onView,
  onDelete,
  onAdd,
  status = 'success',
  error = null,
}) => {
  // Column definitions with action handlers
  const columns = useMemo(
    () =>
      getEmailColumns({
        onView,
        onDelete,
      }),
    [onView, onDelete]
  );

  return (
    <section
      aria-labelledby="email-notifications-heading"
      data-testid="email-notifications-section"
    >
      <SettingContentCard padding="sm">
        <div className="flex flex-col gap-4">
          {/* Header Row - ALWAYS visible */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="size-5 shrink-0 opacity-50" />
                <div className="flex flex-col gap-1">
                  <h4 id="email-notifications-heading" className="font-bold">
                    Email Notifications
                  </h4>
                  <span className="text-chariot-text-secondary text-xs">
                    Receive security alerts and notifications directly in your
                    inbox
                  </span>
                </div>
              </div>
              {onAdd && (
                <Button
                  onClick={onAdd}
                  size="sm"
                  data-testid="email-notifications-add-button"
                ><PlusCircleIcon className="size-4" />Add Email</Button>
              )}
            </div>
          </div>

          {/* Table Row - Only when data exists */}
          {notifications.length > 0 && (
            <div className="border-chariot-stroke overflow-hidden rounded-lg border">
              <TanStackTable
                name="email-notifications"
                columns={columns}
                data={notifications}
                status={status}
                error={error}
              />
            </div>
          )}
        </div>
      </SettingContentCard>
    </section>
  );
};
